import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, Plus, Pencil, Trash2, ArrowLeft, FileText, CalendarDays, Home, Sun, Plane,
  Users, HeartPulse, ShieldCheck, BookOpen, Building2, Eye, EyeOff, IdCard, Palmtree, Wallet, Coins,
} from "lucide-react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/RichTextEditor";
import CalendarTab from "@/pages/rrhh/CalendarTab";
import TeletrabajoTab from "@/pages/rrhh/TeletrabajoTab";
import VacacionesTab from "@/pages/rrhh/VacacionesTab";
import NominasTab from "@/pages/rrhh/NominasTab";
import ComisionesTab from "@/pages/rrhh/ComisionesTab";
import MiFichaTab from "@/pages/rrhh/MiFichaTab";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { RRHHDocuments } from "@/pages/rrhh/RRHHDocuments";

const ICONS = { FileText, CalendarDays, Home, Sun, Plane, Users, HeartPulse, ShieldCheck, BookOpen, Building2 };
const ICON_OPTIONS = Object.keys(ICONS);
const CATEGORIES = ["Jornada y modelo híbrido", "Vacaciones y ausencias", "Guardias y remuneración", "General"];
const emptyArticle = { title: "", category: "General", body: "", summary: "", icon: "FileText", published: true, order: 0 };

// Grupos superiores del Portal (coinciden con los `view` del menú lateral).
const RRHH_GROUPS = ["informacion", "calendario-anual", "ficha", "comisiones"];
const GROUP_META = {
  informacion: { label: "Información y Normas", icon: BookOpen },
  "calendario-anual": { label: "Calendario Anual", icon: CalendarDays },
  ficha: { label: "Ficha", icon: IdCard },
  comisiones: { label: "Comisiones", icon: Coins },
};
// Compat: mapea nombres antiguos de pestaña (deep links) al nuevo grupo superior.
const TAB_TO_GROUP = {
  portal: "informacion", laboral: "informacion",
  calendario: "calendario-anual", teletrabajo: "calendario-anual", vacaciones: "calendario-anual",
  "mi-ficha": "ficha", nominas: "ficha", equipo: "ficha",
  comisiones: "comisiones",
};

export default function RRHHPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const accessQ = useQuery({ queryKey: ["rrhh-access"], queryFn: async () => (await api.get("/rrhh/access")).data });
  const canManage = !!accessQ.data?.can_manage;
  const hasCommissions = !!accessQ.data?.has_commissions;
  // El menú lateral navega con ?view=; conservamos ?tab= por compatibilidad de deep links antiguos.
  const rawParam = params.get("view") || params.get("tab") || "informacion";
  const activeGroup = RRHH_GROUPS.includes(rawParam) ? rawParam : (TAB_TO_GROUP[rawParam] || "informacion");
  // Sub-pestaña inicial por grupo (respeta el deep link antiguo si aplica).
  const infoSub = rawParam === "laboral" ? "laboral" : "normas";
  const calSub = ["teletrabajo", "vacaciones"].includes(rawParam) ? rawParam : "calendario";
  const fichaSub = rawParam === "nominas" ? "nomina" : (rawParam === "equipo" ? "fichas" : "mi-ficha");
  const goGroup = (v) => navigate(`/app/rrhh?view=${v}`);

  return (
    <div className="space-y-5">
      <Tabs value={activeGroup} onValueChange={goGroup}>
        {/* La navegación superior vive en el menú lateral; aquí solo la cabecera de sección. */}
        <div className="mb-4 flex items-center gap-2.5 border-b pb-3" data-testid="rrhh-section-header">
          {(() => { const Icon = GROUP_META[activeGroup]?.icon || BookOpen; return <Icon className="h-5 w-5 text-primary" />; })()}
          <h2 className="font-heading text-lg font-bold tracking-tight" data-testid="rrhh-section-title">{GROUP_META[activeGroup]?.label || "Portal del empleado"}</h2>
        </div>

        {/* 1 · Información y Normas (Normas/guías + Calendario laboral) */}
        <TabsContent value="informacion" className="mt-4">
          <Tabs defaultValue={infoSub}>
            <TabsList className="border border-border/60 bg-background/60" data-testid="rrhh-subtabs-informacion">
              <TabsTrigger value="normas" data-testid="rrhh-subtab-normas"><BookOpen className="mr-1.5 h-4 w-4" />Normas y guías</TabsTrigger>
              <TabsTrigger value="laboral" data-testid="rrhh-subtab-laboral"><CalendarDays className="mr-1.5 h-4 w-4" />Calendario laboral 2026</TabsTrigger>
            </TabsList>
            <TabsContent value="normas" className="mt-4"><PortalTab /></TabsContent>
            <TabsContent value="laboral" className="mt-4"><HolidaysSection canManage={canManage} /></TabsContent>
          </Tabs>
        </TabsContent>

        {/* 2 · Calendario Anual (Calendario + Teletrabajo + Vacaciones) */}
        <TabsContent value="calendario-anual" className="mt-4">
          <Tabs defaultValue={calSub}>
            <TabsList className="border border-border/60 bg-background/60" data-testid="rrhh-subtabs-calendario">
              <TabsTrigger value="calendario" data-testid="rrhh-subtab-calendario"><Building2 className="mr-1.5 h-4 w-4" />Calendario</TabsTrigger>
              <TabsTrigger value="teletrabajo" data-testid="rrhh-subtab-teletrabajo"><Plane className="mr-1.5 h-4 w-4" />Teletrabajo</TabsTrigger>
              <TabsTrigger value="vacaciones" data-testid="rrhh-subtab-vacaciones"><Palmtree className="mr-1.5 h-4 w-4" />Vacaciones</TabsTrigger>
            </TabsList>
            <TabsContent value="calendario" className="mt-4"><CalendarTab /></TabsContent>
            <TabsContent value="teletrabajo" className="mt-4"><TeletrabajoTab /></TabsContent>
            <TabsContent value="vacaciones" className="mt-4"><VacacionesTab /></TabsContent>
          </Tabs>
        </TabsContent>

        {/* 3 · Ficha (Mi ficha + Mi nómina — sin gestión de otros empleados) */}
        <TabsContent value="ficha" className="mt-4">
          <Tabs defaultValue={fichaSub}>
            <TabsList className="border border-border/60 bg-background/60" data-testid="rrhh-subtabs-ficha">
              <TabsTrigger value="mi-ficha" data-testid="rrhh-subtab-mi-ficha"><IdCard className="mr-1.5 h-4 w-4" />Mi ficha</TabsTrigger>
              <TabsTrigger value="nomina" data-testid="rrhh-subtab-nomina"><Wallet className="mr-1.5 h-4 w-4" />Mi nómina</TabsTrigger>
            </TabsList>
            <TabsContent value="mi-ficha" className="mt-4"><MiFichaTab /></TabsContent>
            <TabsContent value="nomina" className="mt-4"><NominasTab canManage={false} canViewEvolucion={false} /></TabsContent>
          </Tabs>
        </TabsContent>

        {/* 4 · Comisiones (solo para quien las tiene) */}
        {hasCommissions && <TabsContent value="comisiones" className="mt-4"><ComisionesTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function PortalTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null); // article being edited/created
  const [confirmDel, setConfirmDel] = useState(null);

  const listQ = useQuery({
    queryKey: ["rrhh-articles", q],
    queryFn: async () => (await api.get("/rrhh/articles", { params: { q: q.trim() || undefined } })).data,
  });
  const data = listQ.data;
  const articles = useMemo(() => data?.articles || [], [data]);
  const canManage = !!data?.can_manage;

  const grouped = useMemo(() => {
    const g = {};
    articles.forEach((a) => { (g[a.category] = g[a.category] || []).push(a); });
    const cats = [...CATEGORIES.filter((c) => g[c]), ...Object.keys(g).filter((c) => !CATEGORIES.includes(c))];
    return cats.map((c) => [c, g[c]]);
  }, [articles]);

  const saveMut = useMutation({
    mutationFn: async (a) => a.id
      ? (await api.put(`/rrhh/articles/${a.id}`, a)).data
      : (await api.post("/rrhh/articles", a)).data,
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["rrhh-articles"] });
      setEditor(null);
      if (selected?.id === saved.id) setSelected(saved);
      toast.success("Contenido guardado");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/rrhh/articles/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rrhh-articles"] });
      setConfirmDel(null); setSelected(null);
      toast.success("Contenido eliminado");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // ---- Vista de artículo ----
  if (selected) {
    const Icon = ICONS[selected.icon] || FileText;
    return (
      <div className="mx-auto max-w-3xl space-y-4" data-testid="rrhh-article-view">
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" onClick={() => setSelected(null)} data-testid="rrhh-article-back"><ArrowLeft className="mr-1.5 h-4 w-4" />Volver</Button>
          {canManage && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditor(selected)} data-testid="rrhh-article-edit"><Pencil className="mr-1.5 h-4 w-4" />Editar</Button>
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setConfirmDel(selected)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
        <Card>
          <CardContent className="p-6 sm:p-8">
            <div className="mb-4 flex items-center gap-3 border-b pb-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
              <div>
                <h1 className="font-heading text-2xl font-bold leading-tight">{selected.title}</h1>
                <p className="text-xs text-muted-foreground">{selected.category}{selected.updated_by ? ` · actualizado por ${selected.updated_by}` : ""}</p>
              </div>
              {!selected.published && <Badge variant="outline" className="ml-auto border-amber-200 bg-amber-50 text-amber-700">Borrador</Badge>}
            </div>
            <div className="hr-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.body || "") }} />
          </CardContent>
        </Card>
        {editor && <ArticleEditor article={editor} onClose={() => setEditor(null)} onSave={(a) => saveMut.mutate(a)} saving={saveMut.isPending} />}
        <DeleteDialog item={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={() => delMut.mutate(confirmDel.id)} />
      </div>
    );
  }

  // ---- Portal (listado) ----
  return (
    <div className="space-y-6" data-testid="rrhh-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><BookOpen className="h-6 w-6 text-primary" />Portal del empleado</h1>
          <p className="text-sm text-muted-foreground">Normas, guías e información de la empresa. Busca tu duda o navega por secciones.</p>
        </div>
        {canManage && <Button onClick={() => setEditor({ ...emptyArticle })} data-testid="rrhh-new-btn"><Plus className="mr-1.5 h-4 w-4" />Nuevo contenido</Button>}
      </div>

      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar una duda… (p. ej. teletrabajo, vacaciones, guardias)" className="h-11 pl-9" data-testid="rrhh-search" />
      </div>

      <RRHHDocuments />

      {listQ.isLoading ? (
        <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-28 w-full" /></div>
      ) : articles.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="rrhh-empty">{q ? "No se encontraron resultados para tu búsqueda." : "Aún no hay contenidos."}</CardContent></Card>
      ) : (
        <div className="space-y-7">
          {grouped.map(([cat, items]) => (
            <section key={cat} data-testid={`rrhh-cat-${cat}`}>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{cat}</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((a) => {
                  const Icon = ICONS[a.icon] || FileText;
                  return (
                    <div key={a.id} onClick={() => setSelected(a)} data-testid={`rrhh-card-${a.id}`}
                      className="group flex cursor-pointer flex-col rounded-xl border bg-card p-4 text-left transition-all hover:border-primary/40 hover:shadow-sm">
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground"><Icon className="h-5 w-5" /></span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="truncate font-semibold leading-tight">{a.title}</h3>
                            {!a.published && <EyeOff className="h-3.5 w-3.5 shrink-0 text-amber-500" title="Borrador" />}
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{a.summary || "Ver detalle"}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                        data-testid={`rrhh-card-full-${a.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 self-start rounded-md px-1 text-xs font-semibold text-primary transition-colors hover:underline">
                        <FileText className="h-3.5 w-3.5" />Ver documento completo
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {editor && <ArticleEditor article={editor} onClose={() => setEditor(null)} onSave={(a) => saveMut.mutate(a)} saving={saveMut.isPending} />}
      <DeleteDialog item={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={() => delMut.mutate(confirmDel.id)} />
    </div>
  );
}

const HOL_SCOPE_LABEL = { nacional: "Nacional", autonomico: "C. Madrid", local: "Madrid" };
const HOL_SCOPE_CLASS = { nacional: "border-blue-200 bg-blue-50 text-blue-700", autonomico: "border-violet-200 bg-violet-50 text-violet-700", local: "border-rose-200 bg-rose-50 text-rose-700" };
const HOL_WD = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const HOL_MON = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
function holFmt(iso) { const [y, m, d] = iso.split("-").map(Number); const dt = new Date(y, m - 1, d); return `${HOL_WD[dt.getDay()]} ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`; }

function HolidaysSection({ canManage }) {
  const qc = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", scope: "local" });
  const holQ = useQuery({ queryKey: ["rrhh-holidays", year], queryFn: async () => (await api.get("/rrhh/holidays", { params: { year } })).data });
  const rows = holQ.data?.rows || [];
  const addMut = useMutation({
    mutationFn: async (p) => (await api.post("/rrhh/holidays", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-holidays"] }); setAdding(false); setForm({ date: "", name: "", scope: "local" }); toast.success("Festivo guardado"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/rrhh/holidays/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-holidays"] }); toast.success("Festivo eliminado"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <section data-testid="rrhh-holidays">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><CalendarDays className="h-6 w-6 text-primary" />Calendario laboral · Madrid {year}</h1>
          <p className="text-xs text-muted-foreground">Días festivos oficiales de Madrid capital (nacionales, autonómicos y locales).</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y - 1)} data-testid="hol-prev">‹</Button>
          <span className="w-12 text-center text-sm font-semibold" data-testid="hol-year">{year}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y + 1)} data-testid="hol-next">›</Button>
          {canManage && <Button size="sm" variant="outline" className="ml-1 h-8" onClick={() => setAdding((a) => !a)} data-testid="hol-add-btn"><Plus className="mr-1 h-3.5 w-3.5" />Añadir</Button>}
        </div>
      </div>
      {canManage && adding && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border bg-muted/30 p-3" data-testid="hol-add-form">
          <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" className="h-9" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="hol-date" /></div>
          <div className="min-w-[160px] flex-1 space-y-1"><Label className="text-xs">Nombre</Label><Input className="h-9" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej.: San Isidro" data-testid="hol-name" /></div>
          <div className="space-y-1"><Label className="text-xs">Ámbito</Label>
            <Select value={form.scope} onValueChange={(v) => setForm((f) => ({ ...f, scope: v }))}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="hol-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="nacional">Nacional</SelectItem><SelectItem value="autonomico">C. Madrid</SelectItem><SelectItem value="local">Local (Madrid)</SelectItem></SelectContent>
            </Select>
          </div>
          <Button size="sm" className="h-9" onClick={() => addMut.mutate(form)} disabled={!form.date || !form.name.trim() || addMut.isPending} data-testid="hol-save">Guardar</Button>
        </div>
      )}
      {holQ.isLoading ? <Skeleton className="h-24 w-full" /> : rows.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="hol-empty">No hay festivos cargados para {year}.</CardContent></Card>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="hol-list">
          {rows.map((h) => (
            <div key={h.id} className="flex items-center justify-between gap-2 rounded-lg border bg-card p-3" data-testid={`hol-${h.date}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <span className="text-sm font-bold leading-none">{h.date.slice(8)}</span>
                  <span className="text-[9px] uppercase leading-none">{HOL_MON[Number(h.date.slice(5, 7)) - 1]}</span>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium leading-tight">{h.name}</p>
                  <p className="text-[11px] capitalize text-muted-foreground">{holFmt(h.date)}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Badge variant="outline" className={HOL_SCOPE_CLASS[h.scope] || HOL_SCOPE_CLASS.local}>{HOL_SCOPE_LABEL[h.scope] || "Local"}</Badge>
                {canManage && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => delMut.mutate(h.id)} data-testid={`hol-del-${h.date}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ArticleEditor({ article, onClose, onSave, saving }) {
  const [form, setForm] = useState(article);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="rrhh-editor-dialog">
        <DialogHeader><DialogTitle>{article.id ? "Editar contenido" : "Nuevo contenido"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Título</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Título de la sección" data-testid="rrhh-form-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="rrhh-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Icono</Label>
              <Select value={form.icon} onValueChange={(v) => set("icon", v)}>
                <SelectTrigger data-testid="rrhh-form-icon"><SelectValue /></SelectTrigger>
                <SelectContent>{ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Resumen (opcional)</Label><Input value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="Breve descripción para la tarjeta" data-testid="rrhh-form-summary" /></div>
          <div className="space-y-1.5"><Label>Contenido</Label><RichTextEditor value={form.body} onChange={(v) => set("body", v)} /></div>
          <div className="flex items-center gap-2">
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="rrhh-form-published" />
            <Label className="cursor-pointer">{form.published ? "Publicado (visible para todos)" : "Borrador (solo gestores)"}</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(form)} disabled={saving || !form.title.trim()} data-testid="rrhh-form-save">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ item, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar «{item?.title}»?</AlertDialogTitle>
          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onConfirm} data-testid="rrhh-delete-confirm">Eliminar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
